import { create } from 'zustand'
import { devtools, persist, createJSONStorage } from 'zustand/middleware'
import type { RBACUser, RBACSession, RoleName, Permission } from '../types/rbac'
import { sanitizeUserPermissions, sanitizeUserRoles, getPermissionsForRoles } from '../utils/rbac-utils'
import { shallow } from 'zustand/shallow'

interface SessionCache {
  data: { user?: any; session?: any } | null
  timestamp: number
  ttl: number
}

interface AuthState {
  user: RBACUser | null
  session: RBACSession | null
  roles: RoleName[]
  permissions: Permission[]
  isLoading: boolean
  isAuthenticated: boolean
  lastRefresh: number
  sessionCache: SessionCache | null
  
  // Optimized actions
  setFromSession: (
    sessionData: { user?: any; session?: any } | null,
    isPending: boolean
  ) => void
  setSessionCache: (data: any, ttl?: number) => void
  getSessionFromCache: () => any | null
  isSessionCacheValid: () => boolean
  shouldRefreshSession: () => boolean
  clear: () => void
  
  // Performance helpers
  hasRole: (role: RoleName) => boolean
  hasPermission: (resource: string, action: string) => boolean
  hasAnyRole: (roles: RoleName[]) => boolean
  hasAllRoles: (roles: RoleName[]) => boolean
}

// Cache and refresh constants
const SESSION_CACHE_TTL = 5 * 60 * 1000; // 5 minutes
const SESSION_REFRESH_INTERVAL = 60 * 60 * 1000; // 1 hour

const initialState: Omit<AuthState, 'setFromSession' | 'setSessionCache' | 'getSessionFromCache' | 'isSessionCacheValid' | 'shouldRefreshSession' | 'clear' | 'hasRole' | 'hasPermission' | 'hasAnyRole' | 'hasAllRoles'> = {
  user: null,
  session: null,
  roles: [] as RoleName[],
  permissions: [] as Permission[],
  isLoading: true,
  isAuthenticated: false,
  lastRefresh: 0,
  sessionCache: null,
}

export function transformUser(betterAuthUser: any): RBACUser | null {
  if (!betterAuthUser) return null

  let roles: RoleName[] = []
  let permissions: Permission[] = []

  // Parse roles
  if (betterAuthUser.roles) {
    if (typeof betterAuthUser.roles === 'string') {
      try { roles = sanitizeUserRoles(JSON.parse(betterAuthUser.roles)) } catch { roles = sanitizeUserRoles([betterAuthUser.roles]) }
    } else if (Array.isArray(betterAuthUser.roles)) {
      roles = sanitizeUserRoles(betterAuthUser.roles)
    }
  }
  if (roles.length === 0) roles = ['user']

  // Parse permissions
  if (betterAuthUser.permissions) {
    if (typeof betterAuthUser.permissions === 'string') {
      try { permissions = sanitizeUserPermissions(JSON.parse(betterAuthUser.permissions)) } catch { permissions = [] }
    } else if (Array.isArray(betterAuthUser.permissions)) {
      permissions = sanitizeUserPermissions(betterAuthUser.permissions)
    }
  }
  if (permissions.length === 0) permissions = getPermissionsForRoles(roles)

  return {
    id: betterAuthUser.id,
    email: betterAuthUser.email,
    fullName: betterAuthUser.fullName || `${betterAuthUser.firstName ?? ''} ${betterAuthUser.lastName ?? ''}`.trim(),
    firstName: betterAuthUser.firstName ?? '',
    lastName: betterAuthUser.lastName ?? '',
    middleName: betterAuthUser.middleName,
    suffix: betterAuthUser.suffix,
    image: betterAuthUser.image,
    phone: betterAuthUser.phone,
    customFields: betterAuthUser.customFields,
    isActive: betterAuthUser.isActive ?? true,
    cartId: betterAuthUser.cartId,
    defaultConversationId: betterAuthUser.defaultConversationId,
    username: betterAuthUser.username,
    displayUsername: betterAuthUser.displayUsername,
    roles,
    permissions,
    createdAt: betterAuthUser.createdAt ? new Date(betterAuthUser.createdAt) : undefined,
    updatedAt: betterAuthUser.updatedAt ? new Date(betterAuthUser.updatedAt) : undefined,
  }
}

export function transformSession(betterAuthSession: any, rbacUser: RBACUser): RBACSession | null {
  if (!betterAuthSession || !rbacUser) return null

  // Prefer augmentation on session
  let roles: RoleName[] = Array.isArray(betterAuthSession?.roles)
    ? sanitizeUserRoles(betterAuthSession.roles as string[])
    : []
  let permissions: Permission[] = Array.isArray(betterAuthSession?.permissions)
    ? sanitizeUserPermissions(betterAuthSession.permissions as any[])
    : []

  if (roles.length === 0) roles = rbacUser.roles?.length ? rbacUser.roles : ['user']
  if (permissions.length === 0) permissions = rbacUser.permissions?.length ? rbacUser.permissions : getPermissionsForRoles(roles)

  return {
    user: rbacUser,
    roles,
    permissions,
    expiresAt: new Date(betterAuthSession.expiresAt),
    token: betterAuthSession.token || '',
  }
}


export const useAuthStore = create<AuthState>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,
        
        setFromSession: (sessionData, isPending) => {
          if (isPending) {
            set({ isLoading: true }, false, 'setFromSession:loading')
            return
          }

          const now = Date.now()

          if (!sessionData?.user || !sessionData?.session) {
            try { sessionStorage.removeItem('TOKEN') } catch { }
            set({ 
              ...initialState, 
              isLoading: false,
              sessionCache: null 
            }, false, 'setFromSession:clear')
            return
          }

          const user = transformUser(sessionData.user)
          if (!user) {
            set({ 
              ...initialState, 
              isLoading: false,
              sessionCache: null 
            }, false, 'setFromSession:invalidUser')
            return
          }

          const session = transformSession(sessionData.session, user)
          const roles: RoleName[] = session?.roles ?? user.roles ?? ['user']
          const permissions: Permission[] = session?.permissions ?? user.permissions ?? getPermissionsForRoles(roles)

          // Persist token for TRPC/http headers compatibility
          try {
            if (session?.token) {
              sessionStorage.setItem('TOKEN', session.token)
            } else {
              sessionStorage.removeItem('TOKEN')
            }
          } catch { }

          set({
            user,
            session,
            roles,
            permissions,
            isLoading: false,
            isAuthenticated: !!user && !!session,
            lastRefresh: now,
          }, false, 'setFromSession:success')
        },

        setSessionCache: (data, ttl = SESSION_CACHE_TTL) => {
          set({
            sessionCache: {
              data,
              timestamp: Date.now(),
              ttl,
            }
          }, false, 'setSessionCache')
        },

        getSessionFromCache: () => {
          const { sessionCache, isSessionCacheValid } = get()
          if (!sessionCache || !isSessionCacheValid()) return null
          return sessionCache.data
        },

        isSessionCacheValid: () => {
          const { sessionCache } = get()
          if (!sessionCache) return false
          return Date.now() - sessionCache.timestamp < sessionCache.ttl
        },

        shouldRefreshSession: () => {
          const { lastRefresh } = get()
          return Date.now() - lastRefresh > SESSION_REFRESH_INTERVAL
        },

        clear: () => {
          try { sessionStorage.removeItem('TOKEN') } catch { }
          set({ 
            ...initialState, 
            isLoading: false,
            sessionCache: null 
          }, false, 'clear')
        },

        // Performance helpers with memoization
        hasRole: (role) => {
          const { roles } = get()
          return roles.includes(role)
        },

        hasPermission: (resource, action) => {
          const { permissions } = get()
          return permissions.some(p => p.resource === resource && p.action === action)
        },

        hasAnyRole: (rolesToCheck) => {
          const { roles } = get()
          return rolesToCheck.some(role => roles.includes(role))
        },

        hasAllRoles: (rolesToCheck) => {
          const { roles } = get()
          return rolesToCheck.every(role => roles.includes(role))
        },
      }),
      {
        name: 'dealz-auth-store',
        storage: createJSONStorage(() => localStorage),
        // Only persist essential user data, not sensitive session tokens
        partialize: (state) => ({
          user: state.user,
          isAuthenticated: state.isAuthenticated,
          lastRefresh: state.lastRefresh,
          roles: state.roles,
          permissions: state.permissions,
        }),
        // Custom serialization for dates
        serialize: (state) => {
          return JSON.stringify({
            ...state,
            user: state.user ? {
              ...state.user,
              createdAt: state.user.createdAt?.toISOString(),
              updatedAt: state.user.updatedAt?.toISOString(),
            } : null,
          });
        },
        deserialize: (str) => {
          const state = JSON.parse(str);
          return {
            ...state,
            user: state.user ? {
              ...state.user,
              createdAt: state.user.createdAt ? new Date(state.user.createdAt) : undefined,
              updatedAt: state.user.updatedAt ? new Date(state.user.updatedAt) : undefined,
            } : null,
          };
        },
      }
    ),
    {
      name: 'auth-store',
    }
  )
)



export const r = (sessionData: any) => {
  if (!sessionData?.user || !sessionData?.session) {
    try { sessionStorage.removeItem('TOKEN') } catch { }
    return { ...initialState, isLoading: false }
  }

  const user = transformUser(sessionData.user)
  if (!user) {
    return { ...initialState, isLoading: false }
  }

  const session = transformSession(sessionData.session, user)
  const roles: RoleName[] = session?.roles ?? user.roles ?? ['user']
  const permissions: Permission[] = session?.permissions ?? user.permissions ?? getPermissionsForRoles(roles)

  // Persist token for TRPC/http headers compatibility
  try {
    if (session?.token) {
      sessionStorage.setItem('TOKEN', session.token)
    } else {
      sessionStorage.removeItem('TOKEN')
    }
  } catch { }

  return {
    user,
    session,
    roles,
    permissions,
    isLoading: false,
    isAuthenticated: !!user && !!session,
  }
}